import { FunctionComponent } from 'react';
import { Stack } from '@spotify-internal/encore-web/ads-business';
import {
  Type,
  spacer24,
  spacer32,
  spacer8,
} from '@spotify-internal/encore-web/core';
import { useT } from '@spotify-internal/i18n-core';
import {
  ArtistRole,
  Identifier,
  IdentifierType,
} from '@hooks/useAlbumInfo/types';
import {
  AssociatedTrack,
  TrackDetails,
  TrackMediaType,
} from '@hooks/useTrackInfo/types';
import { splitToRows } from '@components/Release/helpers';
import { PillsContainer } from '@components/Release/TracksTab/styles';
import { EVENT_CATEGORY } from '@lib/ga/types';
import { getArtistTranslationKey } from '@helpers/artistUtils';
import { EMPTY_VALUE } from '@helpers/constants';
import { DataTable } from '../DataTable';
import { PillInfoWithHrefAndCopy } from '../PillInfo/PillInfoWithHrefAndCopy';
import { Row, RowCell } from '../styles';
import {
  getMetadataAttributeByReason,
  getReasonDescriptionTranslationKey,
  getReasonTranslationKey,
} from './helpers';

type Metadata = Pick<
  TrackDetails,
  'name' | 'artists' | 'explicitLyrics' | 'uri' | 'mediaType' | 'duration'
>;

export type UnassociatedVideoDetailsProps = {
  associatedTrack: AssociatedTrack;
  isrc?: string;
  identifiers: Identifier[];
  metadata: Metadata;
};

export const UnassociatedVideoDetails: FunctionComponent<
  UnassociatedVideoDetailsProps
> = ({ associatedTrack, isrc, identifiers, metadata }) => {
  const t = useT();
  const { videoAssociation, trackDetails, trackUri } = associatedTrack;
  const {
    deliveryId,
    decisionReason = [],
    audioTrackUri,
  } = videoAssociation ?? {};

  const associatedMetadata = !!trackDetails
    ? {
        name: trackDetails.name,
        mediaType: trackDetails.mediaType,
        explicitLyrics: trackDetails.explicitLyrics,
        uri: trackUri,
        artists: trackDetails.artists,
        licensorName: trackDetails.licensorName,
        duration: trackDetails.duration,
      }
    : undefined;

  const getMetadata = (mediaType: TrackMediaType): Metadata | undefined => {
    switch (mediaType) {
      case metadata.mediaType:
        return metadata;
      case associatedMetadata?.mediaType:
        return associatedMetadata;
      default:
        return undefined;
    }
  };

  const getFormattedMetadataValue = (
    contentMetadata: Metadata,
    attributeKey: keyof Metadata,
  ) => {
    switch (attributeKey) {
      case 'artists':
        return contentMetadata[attributeKey]
          .filter(({ role }) =>
            [
              ArtistRole.MAIN_ARTIST,
              ArtistRole.FEATURED_ARTIST,
              ArtistRole.REMIXER,
            ].includes(role),
          )
          .map(({ name, role: roleEnum }) => {
            const role = getArtistTranslationKey(roleEnum, t);
            return `${name} (${role})`;
          })
          .join(', ');
      case 'explicitLyrics':
        return contentMetadata[attributeKey]
          ? t(
              'atlas.labels.explicit',
              'Explicit',
              'Label for show if the track has Explicit lyrics',
            )
          : t(
              'atlas.labels.cleaned',
              'Cleaned',
              'Label for show if the track has Cleaned lyrics',
            );
      case 'duration':
        return `${Math.floor(
          contentMetadata[attributeKey] / 60000,
        )}:${`0${Math.floor(
          (contentMetadata[attributeKey] % 60000) / 1000,
        )}`.slice(-2)}`;
      default:
        return contentMetadata[attributeKey];
    }
  };

  const reasons = decisionReason.map(reasonEnum => {
    const reason = getReasonTranslationKey(reasonEnum, t);
    const reasonDescription = getReasonDescriptionTranslationKey(reasonEnum, t);
    const metadataAttributeKey = getMetadataAttributeByReason(reasonEnum);

    const videoMetadata = getMetadata(TrackMediaType.MEDIA_TYPE_VIDEO);
    const audioMetadata = getMetadata(TrackMediaType.MEDIA_TYPE_AUDIO);

    const videoMetadataFormattedValue =
      !!videoMetadata && Boolean(videoMetadata) && !!metadataAttributeKey
        ? getFormattedMetadataValue(videoMetadata, metadataAttributeKey)
        : EMPTY_VALUE;
    const audioMetadataFormattedValue =
      !!audioMetadata && Boolean(audioMetadata) && !!metadataAttributeKey
        ? getFormattedMetadataValue(audioMetadata, metadataAttributeKey)
        : EMPTY_VALUE;

    return {
      reason,
      description: reasonDescription,
      videoMetadata: videoMetadataFormattedValue,
      audioMetadata: audioMetadataFormattedValue,
    };
  });

  const trackTabsFields = [
    {
      type: t('atlas.labels.batch_id', 'Batch ID', 'Label for Batch ID'),
      value: deliveryId,
      tooltipText: t(
        `atlas.actions.copy_identifier`,
        `Copy Identifier`,
        `Text for Copy Identifier action`,
      ),
    },
    {
      type: t(
        'atlas.labels.candidate_audio_track',
        'Candidate Audio Track',
        'Label for possible Audio track URI alternative',
      ),
      value: audioTrackUri,
      href: audioTrackUri,
      tooltipText: t(
        `atlas.actions.copy_uri`,
        `Copy URI`,
        `Text for Copy URI action`,
      ),
    },
    {
      type: t(
        'atlas.related_release_isrc',
        'Related release ISRC',
        'The ISRC of a related release',
      ),
      value: isrc,
      tooltipText: t(
        `atlas.actions.copy_isrc`,
        `Copy ISRC`,
        `Text for Copy ISRC action`,
      ),
    },
    {
      type: t(
        'atlas.labels.candidate_audio_isrc',
        'Candidate Audio ISRC',
        'Label for possible Audio ISRC alternative',
      ),
      value: trackDetails?.isrc,
      tooltipText: t(
        `atlas.actions.copy_isrc`,
        `Copy ISRC`,
        `Text for Copy ISRC action`,
      ),
    },
    {
      type: t(
        'atlas.labels.release_upc',
        'Release UPC',
        'Label for release UPC',
      ),
      value: identifiers.find(
        ({ type }) => type.toLocaleUpperCase() === IdentifierType.UPC,
      )?.value,
      tooltipText: t(
        `atlas.actions.copy_upc`,
        `Copy UPC`,
        `Text for Copy UPC action`,
      ),
    },
  ];

  return (
    <Stack gap={spacer32}>
      <PillsContainer>
        {splitToRows(trackTabsFields).map((rowIdentifer, index) => {
          const firstIdentifier = rowIdentifer[0];
          const secondIdentifier = rowIdentifer[1];

          return (
            <Row key={index}>
              <RowCell marginRight>
                <PillInfoWithHrefAndCopy
                  gaEventCategory={EVENT_CATEGORY.RELEASE_TRACKS_TAB}
                  label={firstIdentifier.type}
                  value={firstIdentifier.value || EMPTY_VALUE}
                  href={firstIdentifier.href}
                  copyTooltipPopupText={firstIdentifier.tooltipText}
                  hasBackgroundColor
                />
              </RowCell>
              {secondIdentifier ? (
                <RowCell marginLeft>
                  <PillInfoWithHrefAndCopy
                    gaEventCategory={EVENT_CATEGORY.RELEASE_TRACKS_TAB}
                    label={secondIdentifier.type}
                    value={secondIdentifier.value || EMPTY_VALUE}
                    href={secondIdentifier.href}
                    copyTooltipPopupText={secondIdentifier.tooltipText}
                    hasBackgroundColor
                  />
                </RowCell>
              ) : (
                <RowCell marginLeft />
              )}
            </Row>
          );
        })}
      </PillsContainer>

      <div>
        <Type variant="celloCanon" paddingBottom={spacer8} as="h2">
          {t(
            'atlas.labels.reasons',
            'Reasons',
            'Label for Reasons of the blocked release',
          )}
        </Type>

        <Type variant="viola" paddingBottom={spacer24} as="p">
          {t(
            'atlas.unassociated_videos.reason_description',
            'Review the reasons for this failed association and redeliver.',
            'Reason description unassociated video section',
          )}
        </Type>
      </div>

      <DataTable
        headers={[
          {
            label: t(
              'atlas.labels.reason',
              'Reason',
              'Label for Reason of the blocked release',
            ),
            property: 'reason',
            renderRow: ({ reason }) => (
              <Type variant="violaBold" semanticColor="textBase">
                {reason}
              </Type>
            ),
            width: '25%',
          },
          {
            label: t(
              'atlas.labels.description',
              'Description',
              'Label for Description field',
            ),
            property: 'description',
            width: '25%',
          },
          {
            label: t(
              'atlas.labels.video_track_metadata',
              'Video Track Metadata',
              'Label for video track metadata values',
            ),
            property: 'videoMetadata',
            width: '25%',
          },
          {
            label: t(
              'atlas.labels.audio_track_metadata',
              'Audio Track Metadata',
              'Label for audio track metadata values',
            ),
            property: 'audioMetadata',
            width: '25%',
          },
        ]}
        data={reasons}
      />
    </Stack>
  );
};

export default UnassociatedVideoDetails;
